import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ProcessPartnersService } from '../process-partners.service';
import {
  createProcessPartner,
  createProcessPartnerFailure,
  createProcessPartnerSuccess,
  loadProcessPartnerById,
  loadProcessPartnerByIdFailure,
  loadProcessPartnerByIdSuccess,
  loadProcessPartners,
  loadProcessPartnersFailure,
  loadProcessPartnersSuccess,
  searchProcessPartners,
  searchProcessPartnersFailure,
  searchProcessPartnersSuccess,
  setProcessPartnerInactive,
  setProcessPartnerInactiveSuccess,
  updateProcessPartner,
  updateProcessPartnerFailure,
  updateProcessPartnerSuccess,
} from './process-partners.actions';
import { forkJoin, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { mapProcessPartnerResponseToCreate } from '../process-partners.models';
import { pruneUnsupportedProcessPartnerFields } from '../process-partners.request';

@Injectable()
export class ProcessPartnersEffects {
  private readonly actions$ = inject(Actions);
  private readonly processPartnersService = inject(ProcessPartnersService);

  public loadProcessPartners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProcessPartners),
      switchMap(() =>
        this.processPartnersService.getProcessPartners().pipe(
          map((processPartners) =>
            loadProcessPartnersSuccess({
              processPartners,
              loading: false,
              processPartner: null,
              error: null,
              buttonLoading: false,
            })
          ),
          catchError((error) =>
            of(loadProcessPartnersFailure({ error, loading: false }))
          )
        )
      )
    )
  );

  public setProcessPartnersInactive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setProcessPartnerInactive),
      switchMap(({ processPartners }) => {
        const updateObservables = processPartners.map((processPartner) =>
          this.processPartnersService
            .getProcessPartnerById(processPartner.id as number)
            .pipe(
              switchMap((actualProcessPartner) =>
                this.processPartnersService.updateProcessPartner(
                  actualProcessPartner.id,
                  pruneUnsupportedProcessPartnerFields({
                    ...mapProcessPartnerResponseToCreate(actualProcessPartner),
                    isActive: false,
                  })
                )
              )
            )
        );
        if (updateObservables.length === 0) {
          return of(setProcessPartnerInactiveSuccess());
        }
        return forkJoin(updateObservables).pipe(
          map(() => setProcessPartnerInactiveSuccess()),
          catchError(() => of(setProcessPartnerInactiveSuccess()))
        );
      })
    )
  );

  public searchProcessPartners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(searchProcessPartners),
      switchMap(({ searchObj }) =>
        this.processPartnersService.searchProcessPartners(searchObj).pipe(
          map((processPartners) =>
            searchProcessPartnersSuccess({
              processPartners,
              loading: false,
              error: null,
              processPartner: null,
              buttonLoading: false,
            })
          ),
          catchError((error) =>
            of(searchProcessPartnersFailure({ error, loading: false }))
          )
        )
      )
    )
  );

  public createProcessPartner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createProcessPartner),
      switchMap(({ processPartner }) =>
        this.processPartnersService.createProcessPartner(processPartner).pipe(
          map((processPartner) =>
            createProcessPartnerSuccess({ processPartner })
          ),
          catchError(() => of(createProcessPartnerFailure))
        )
      )
    )
  );

  public getProcessPartnerById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProcessPartnerById),
      switchMap(({ id }) =>
        this.processPartnersService.getProcessPartnerById(id).pipe(
          map((processPartner) => {
            return loadProcessPartnerByIdSuccess({ processPartner });
          }),
          catchError((error) => of(loadProcessPartnerByIdFailure({ error })))
        )
      )
    )
  );
  public updateProcessPartner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProcessPartner),
      switchMap(({ id, processPartner }) =>
        this.processPartnersService
          .updateProcessPartner(
            id,
            pruneUnsupportedProcessPartnerFields(
              mapProcessPartnerResponseToCreate(processPartner)
            )
          )
          .pipe(
            map((processPartner) => {
              return updateProcessPartnerSuccess({ processPartner });
            }),
            catchError(() => of(updateProcessPartnerFailure()))
          )
      )
    )
  );
}
